import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../config/reactQuery';
import agent from '../api/axios';
import { useSnackbar } from 'notistack';

/**
 * Custom hooks for data fetching with React Query
 * Provides automatic caching, loading states, and error handling
 */

// ========== PAYEES ==========

export function usePayees() {
  return useQuery({
    queryKey: queryKeys.payees,
    queryFn: () => agent.Employees.list(),
  });
}

export function useCreatePayee() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: any) => agent.Employees.add(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.payees });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
      enqueueSnackbar('Payee created successfully', { variant: 'success' });
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error?.response?.data?.message || 'Failed to create payee',
        { variant: 'error' }
      );
    },
  });
}

export function useDeletePayee() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => agent.Employees.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.payees });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
      enqueueSnackbar('Payee deleted successfully', { variant: 'success' });
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error?.response?.data?.message || 'Failed to delete payee',
        { variant: 'error' }
      );
    },
  });
}

export function useUpdatePayee() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      agent.Employees.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.payees });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
      enqueueSnackbar('Payee updated successfully', { variant: 'success' });
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error?.response?.data?.message || 'Failed to update payee',
        { variant: 'error' }
      );
    },
  });
}

// ========== DASHBOARD ==========

export function useDashboard() {
  return useQuery({
    queryKey: queryKeys.dashboard,
    queryFn: () => agent.Dashboard.get(),
    staleTime: 2 * 60 * 1000, // Dashboard data is fresh for 2 minutes
  });
}

// ========== TIMESHEETS ==========

export function useSubmitTimesheet() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (timesheet: any) => agent.Timesheets.submit(timesheet),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.timesheets });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
      enqueueSnackbar('Timesheet submitted successfully', {
        variant: 'success',
      });
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error?.response?.data?.message || 'Failed to submit timesheet',
        { variant: 'error' }
      );
    },
  });
}

// ========== BUSINESS / PROFILE ==========

export function useBusiness() {
  return useQuery({
    queryKey: queryKeys.business,
    queryFn: async () => {
      const businesses = await agent.Business.get();
      return businesses[0]; // API returns array with single business
    },
  });
}

export function usePayPeriod() {
  return useQuery({
    queryKey: queryKeys.payPeriod,
    queryFn: () => agent.Business.getCurrentPayPeriod(),
    staleTime: 24 * 60 * 60 * 1000, // Pay period info is fresh for 24 hours
  });
}

export function useUpdateBusiness() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: any) => agent.Business.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.business });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
      enqueueSnackbar('Profile updated successfully', { variant: 'success' });
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error?.response?.data?.message || 'Failed to update profile',
        { variant: 'error' }
      );
    },
  });
}

// ========== TAXES / 1099 ==========

export function useTaxYears() {
  return useQuery({
    queryKey: queryKeys.taxYears,
    queryFn: () => agent.Tax.getTaxYears(),
    staleTime: 60 * 60 * 1000, // Tax years rarely change, cache for 1 hour
  });
}

export function useTaxYear(year: number) {
  return useQuery({
    queryKey: queryKeys.taxYear(year),
    queryFn: () => agent.Tax.getForYear(year),
    enabled: !!year,
  });
}
